import { useLazyQuery } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { XmarkCircleIcon } from '@synoptic/ui-kit/icons/react/xmark-circle.tsx';
import { InputIconButton } from '@synoptic/ui-kit/input/input-icon-button.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { ModalContent } from '@synoptic/ui-kit/modal/modal.js';
import { ModalHeader, ModalTitle } from '@synoptic/ui-kit/modal/modal.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { SubmitHandler, useForm } from 'react-hook-form';
import { gql } from '@/__generated__';
import { streamFormActions } from '@/components/streams/stream-form/stream-form.css.ts';
import {
  formContainer,
  dateRangeContainer,
} from './export-stream-posts-modal.css.ts';

const ExportStreamPostsQuery = gql(`
query ExportStreamPosts($input: ExportStreamPostsInput!) {
  exportStreamPostsUrl(input: $input)
}
`);

type ExportFormData = {
  fromDate?: string;
  toDate?: string;
};

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Clamps to the last day of the previous month to avoid `setMonth` overflow.
const subtractOneMonth = (date: Date) => {
  const result = new Date(date);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() - 1);
  const lastDayOfPrevMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0,
  ).getDate();
  result.setDate(Math.min(day, lastDayOfPrevMonth));
  return result;
};

const getDefaultDateRange = () => ({
  fromDate: formatDateInputValue(subtractOneMonth(new Date())),
  toDate: formatDateInputValue(new Date()),
});

// Treats the picker value as a local-time boundary so the ISO cutoff is
// correct regardless of the user's timezone offset.
const parseDateInputAsLocalIso = (
  dateStr: string,
  boundary: 'start' | 'end',
) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date =
    boundary === 'start'
      ? new Date(year, month - 1, day, 0, 0, 0, 0)
      : new Date(year, month - 1, day, 23, 59, 59, 999);
  return date.toISOString();
};

export const ExportStreamPostsModal = ({
  onOpenChange,
  streamId,
}: {
  onOpenChange: (open: boolean) => void;
  streamId: string;
}) => {
  const toast = useToast();

  const { handleSubmit, formState, register, getValues, setValue, watch } =
    useForm<ExportFormData>({
      mode: 'onChange',
      defaultValues: getDefaultDateRange(),
    });

  const fromDateValue = watch('fromDate');
  const toDateValue = watch('toDate');

  const clearField = (field: keyof ExportFormData) => {
    setValue(field, '', { shouldValidate: true, shouldDirty: true });
  };

  const [exportStreamPosts] = useLazyQuery(ExportStreamPostsQuery, {
    fetchPolicy: 'network-only',
  });

  const submit: SubmitHandler<ExportFormData> = async (data) => {
    const fromDate = data.fromDate
      ? parseDateInputAsLocalIso(data.fromDate, 'start')
      : undefined;
    const toDate = data.toDate
      ? parseDateInputAsLocalIso(data.toDate, 'end')
      : undefined;

    const result = await exportStreamPosts({
      variables: {
        input: {
          streamId,
          fromDate,
          toDate,
        },
      },
    });

    if (result.data) {
      toast.success({
        title: 'Export posts started',
        subTitle: 'You will be notified with a url when the export is ready',
      });
      onOpenChange(false);
    } else if (result.error) {
      toast.error({
        title: 'Failed to export posts',
        subTitle: result.error.message,
      });
    }
  };

  return (
    <ModalContent>
      <ModalHeader>
        <ModalTitle>Export stream posts</ModalTitle>
      </ModalHeader>

      <form onSubmit={handleSubmit(submit)} className={formContainer}>
        <div className={dateRangeContainer}>
          <Input
            {...register('fromDate', {
              deps: ['toDate'],
              validate: (value) => {
                const { toDate } = getValues();
                if (value && toDate) {
                  const fromDate = new Date(value);
                  const toDateObj = new Date(toDate);
                  if (fromDate > toDateObj) {
                    return 'From date must be before to date';
                  }
                }
                return true;
              },
            })}
            type="date"
            label="From date"
            markOptional
            errorMessage={formState.errors.fromDate?.message}
            placeholder="Leave empty to export from the beginning"
            after={
              fromDateValue ? (
                <InputIconButton
                  aria-label="Clear from date"
                  onClick={() => clearField('fromDate')}
                >
                  <XmarkCircleIcon />
                </InputIconButton>
              ) : null
            }
          />

          <Input
            {...register('toDate', {
              deps: ['fromDate'],
              validate: (value) => {
                const { fromDate } = getValues();
                if (value && fromDate) {
                  const fromDateObj = new Date(fromDate);
                  const toDateObj = new Date(value);
                  if (toDateObj < fromDateObj) {
                    return 'To date must be after from date';
                  }
                }
                return true;
              },
            })}
            type="date"
            label="To date"
            markOptional
            errorMessage={formState.errors.toDate?.message}
            placeholder="Leave empty to export until now"
            after={
              toDateValue ? (
                <InputIconButton
                  aria-label="Clear to date"
                  onClick={() => clearField('toDate')}
                >
                  <XmarkCircleIcon />
                </InputIconButton>
              ) : null
            }
          />
        </div>

        <div className={streamFormActions}>
          <Button
            disabled={formState.isSubmitting}
            type="button"
            variant={'secondary'}
            size={'medium'}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>

          <Button
            disabled={!formState.isValid || formState.isSubmitting}
            type="submit"
            size={'medium'}
            loading={formState.isSubmitting}
          >
            Export
          </Button>
        </div>
      </form>
    </ModalContent>
  );
};
